<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
         </div>

      <div class="modal__main-icon modal__main-icon--model"></div>
      <div class="modal__back" @click="goBack()"></div>
      <div class="modal__close" @click="closeModal()"></div>

      <div class="form">
        <div class="navigators">
          <div class="navigator navigator--active">
            <div class="navigator__bullet"></div>
            <div class="navigator__title">{{ $t(`addModelModal.${"title"}`) }}</div>
          </div>
        </div>
        <div class="add__form">
          <transition name="fade" mode="out-in">
            <div class="add__content">
              <div class="form__field">
                <div
                  class="input input--default input--readonly"
                  :class="{ 'select--error': companyHasError }"
                >
                  {{ selectedCompany.name }}
                </div>
                <div class="form__error" v-if="companyHasError">
                  {{ $t(`addModelModal.${"companyErrorMsg"}`) }}
                </div>
              </div>
              <div class="form__field">
                <div
                  class="input input--default input--readonly"
                  :class="{ 'select--error': serviceHasError }"
                >
                  {{ selectedService[`long_${language}`] }}
                </div>
                <div class="form__error" v-if="serviceHasError">
                  {{ $t(`addModelModal.${"serviceErrorMsg"}`) }}
                </div>
              </div>
              <div class="form__field">
                <input
                  type="text"
                  class="input input--default"
                  :class="{ 'input--error': nameHasError }"
                  placeholder="Name"
                  v-model="model.name"
                />
                <div class="form__error" v-if="nameHasError">
                  {{ $t(`addModelModal.${"nameErrorMsg"}`) }}
                </div>
              </div>
              <div class="form__field">
                <input
                  type="text"
                  class="input input--default"
                  :placeholder="$t(`addModelModal.${'cantonPlaceholder'}`)" 
                  v-model="model.canton"
                />
              </div>
              <div class="form__field">
                <input
                  type="text"
                  class="input input--default"
                  placeholder="Region"
                  v-model="model.region"
                />
              </div>
              <div
                class="form__field"
                v-for="(category, index) in model.categories"
                :key="index"
              >
                <input
                  type="text"
                  class="input input--default"
                  :placeholder="$t(`addModelModal.${'categoryPlaceholder'}`)" 
                  v-model="category.name"
                />
                <div
                  class="form__add"
                  v-if="index === 0 && category.name"
                  @click="addCategory()"
                ></div>
                <div
                  class="form__remove"
                  v-else-if="index !== 0"
                  @click="removeCategory(index)"
                ></div>
              </div>
            </div>
          </transition>
        </div>
      </div>
      <div
        v-if="isRequesting"
        class="modal__button modal__button--submit button button--default button--loading"
      ></div>
      <button
        v-else
        class="modal__button modal__button--submit button button--default"
        @click="createModel()"
      >
        <span>{{ $t(`addModelModal.${"saveBtn"}`) }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";

defineOptions({
  name: "AddModelModal"
});

const store = useStore();

// Reactive State
const currentPage = ref(0);
const isRequesting = ref(false);

const model = ref({
  companyId: "",
  serviceId: "",
  name: "",
  canton: "",
  region: "",
  categories: [{ name: "" }],
});

const companyHasError = ref(false);
const serviceHasError = ref(false);
const nameHasError = ref(false);

// Computed properties (Vuex Getters)
const selectedCompany = computed(() => store.getters.selectedCompany);
const selectedService = computed(() => store.getters.selectedService);
const language = computed(() => store.getters.language);

// Watchers
watch(
  () => model.value.name,
  (value) => {
    nameHasError.value = !value;
  }
);

watch(
  () => model.value.companyId,
  (companyId) => {
    companyHasError.value = !companyId;
  }
);

watch(
  () => model.value.serviceId,
  (serviceId) => {
    serviceHasError.value = !serviceId;
  }
);

// Lifecycle Hooks (Replaces mounted)
onMounted(() => {
  model.value.companyId = selectedCompany.value.id;
  model.value.serviceId = selectedService.value.id;
});

// Methods
const closeModal = () => {
  store.commit("setShowAddModelModal", false);
};

const goBack = () => {
  currentPage.value--;
  if (currentPage.value < 0) {
    closeModal();
    store.commit("setShowAddCustomerServicesModal", true);
  }
};

const addCategory = () => {
  model.value.categories = [...model.value.categories, { name: "" }];
};

const removeCategory = (index) => {
  model.value.categories.splice(index, 1);
};

const validateFields = () => {
  const { companyId, serviceId, name } = model.value;

  if (!companyId) {
    companyHasError.value = true;
  }

  if (!serviceId) {
    serviceHasError.value = true;
  }

  if (!name) {
    nameHasError.value = true;
  }

  return !companyHasError.value && !serviceHasError.value && !nameHasError.value;
};

const createModel = async () => {
  isRequesting.value = true;
  if (validateFields()) {
    const {
      companyId,
      serviceId,
      name,
      canton,
      region,
      categories,
    } = model.value;
    
    const modelCategories = categories.filter((category) => category.name);
    
    if (modelCategories.length) {
      await store.dispatch("addModel", {
        companyId,
        serviceId,
        name,
        canton,
        region,
        categories: modelCategories,
      });
    } else {
      await store.dispatch("addModel", {
        companyId,
        serviceId,
        name,
        canton,
        region,
      });
    }
    
    const models = await store.dispatch(
      "getModelsByCompanyAndServices",
      { companyId, serviceId }
    );
    
    store.commit("setModels", models);
    closeModal();
  }
  isRequesting.value = false;
};
</script>